import { useState } from "react";
import { Clock3, CheckCircle2, XCircle, Clock, FileText, ChevronDown, ChevronRight, Trash2, Eye, FileEdit, AlertCircle, Download, UserPlus, Search, RefreshCw, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { PageLoader } from "@/components/ui/Spinner";
import { useTimesheets, useApproveTimesheet, useRejectTimesheet, useDeleteTimesheet, useDraftsOverview, useSubmissionStats, useTimesheetMissing, useProxyTargets, useApproveWithdrawal, useRejectWithdrawal } from "../hooks/useTimesheets";
import { TIMESHEET_STATUS_COLORS, MONTH_NAMES, type Timesheet, type TimesheetStatus } from "../types";
import { DraftPreviewModal } from "../components/DraftPreviewModal";
import { TimesheetPdfModal } from "../components/TimesheetPdfModal";
import { ProxySubmitModal } from "../components/ProxySubmitModal";
import { RevokeApprovalModal } from "../components/RevokeApprovalModal";
import { useAuthStore } from "@/store/auth";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import type { ProxyTarget } from "../api/timesheets";
import { SearchableSelect } from "@/components/ui/SearchableSelect";

type StatusFilter = "all" | "pending" | "withdrawal_requested" | "approved" | "rejected";
type ViewMode = "submitted" | "drafts" | "missing";

const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_MONTH = new Date().getMonth() + 1;

function EmployeePickerContent({
  targets,
  search,
  onSearchChange,
  onSelect,
  onCancel,
}: {
  targets: ProxyTarget[];
  search: string;
  onSearchChange: (v: string) => void;
  onSelect: (t: ProxyTarget) => void;
  onCancel: () => void;
}) {
  const q = search.toLowerCase();
  const filtered = q ? targets.filter((t) => t.full_name.toLowerCase().includes(q)) : targets;
  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <input
          autoFocus
          type="text"
          className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
          placeholder="Szukaj po nazwisku..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>
      <ul className="divide-y divide-gray-100 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
        {filtered.length === 0 ? (
          <li className="px-4 py-3 text-sm text-gray-400 dark:text-gray-500 text-center">Brak wyników.</li>
        ) : (
          filtered.map((t) => (
            <li key={t.id}>
              <button
                className="w-full text-left px-4 py-2.5 text-sm text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                onClick={() => onSelect(t)}
              >
                {t.full_name}
                {!t.is_active && (
                  <span className="ml-2 text-xs text-amber-600 dark:text-amber-400">(nieaktywny)</span>
                )}
              </button>
            </li>
          ))
        )}
      </ul>
      <div className="flex justify-end">
        <Button variant="secondary" onClick={onCancel}>Anuluj</Button>
      </div>
    </div>
  );
}

interface DraftPreviewTarget {
  employeeId: string;
  employeeName: string;
  secondaryContractId: string | null;
  secondaryContractKind: string | null;
}

export function TimesheetReviewPage() {
  const user = useAuthStore((s) => s.user);
  const isDirector = user?.role === "director";
  const isCoordinator = user?.role === "coordinator";
  // Undoing an already-granted approval is reserved for the CEO — a plain
  // director may only approve/reject cards that are still pending.
  const isCeo = !!user?.is_ceo;
  // Contractors are out of the timesheet system, so the coordinator (whose only
  // proxy power was filing cards for contractors) can no longer submit on behalf.
  const canProxy = isDirector || user?.role === "accountant" || user?.is_ceo;

  const { data: internalUsers = [] } = useInternalUsers();
  const { data: proxyTargets = [] } = useProxyTargets(canProxy);

  // On-behalf PDF modal state
  const [proxyPdfEmployee, setProxyPdfEmployee] = useState<{ id: string; name: string } | null>(null);
  const [proxyPdfPickerOpen, setProxyPdfPickerOpen] = useState(false);
  const [proxyPdfSearch, setProxyPdfSearch] = useState("");

  // On-behalf submit modal state
  const [proxySubmitEmployee, setProxySubmitEmployee] = useState<{ id: string; name: string } | null>(null);
  const [proxySubmitPickerOpen, setProxySubmitPickerOpen] = useState(false);
  const [proxySubmitSearch, setProxySubmitSearch] = useState("");

  const [viewMode, setViewMode] = useState<ViewMode>("submitted");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("pending");
  const [yearFilter, setYearFilter] = useState<string>(String(CURRENT_YEAR));
  const [monthFilter, setMonthFilter] = useState<string>("");
  const [employeeFilter, setEmployeeFilter] = useState<string>("");

  // Drafts and missing modes require a concrete (year, month); fall back to current month.
  const draftYear = Number(yearFilter) || CURRENT_YEAR;
  const draftMonth = Number(monthFilter) || CURRENT_MONTH;

  const params: Record<string, string> = { review: "1" };
  if (statusFilter !== "all") params.status = statusFilter;
  if (yearFilter) params.year = yearFilter;
  if (monthFilter) params.month = monthFilter;
  if (employeeFilter) params.employee = employeeFilter;

  const { data: timesheets = [], isLoading } = useTimesheets(
    viewMode === "submitted" ? params : undefined,
  );
  const { data: draftRows = [], isLoading: draftsLoading } = useDraftsOverview(
    draftYear,
    draftMonth,
    viewMode === "drafts",
  );
  const { data: missingRows = [], isLoading: missingLoading } = useTimesheetMissing(
    draftYear,
    draftMonth,
    viewMode === "missing",
  );

  // Submission stats: only when a specific month is selected (not "all months")
  const statsMonth = Number(monthFilter) || (viewMode !== "submitted" ? draftMonth : 0);
  const statsEnabled = statsMonth >= 1 && statsMonth <= 12;
  const statsYear = Number(yearFilter) || CURRENT_YEAR;
  const { data: submissionStats } = useSubmissionStats(statsYear, statsMonth, statsEnabled && !isCoordinator);
  const approveTimesheet = useApproveTimesheet();
  const rejectTimesheet = useRejectTimesheet();
  const deleteTimesheet = useDeleteTimesheet();
  const approveWithdrawal = useApproveWithdrawal();
  const rejectWithdrawal = useRejectWithdrawal();
  const [draftPreview, setDraftPreview] = useState<DraftPreviewTarget | null>(null);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [approveModal, setApproveModal] = useState<Timesheet | null>(null);
  const [approveComment, setApproveComment] = useState("");
  const [approveError, setApproveError] = useState("");
  const [rejectModal, setRejectModal] = useState<Timesheet | null>(null);
  const [rejectComment, setRejectComment] = useState("");
  const [rejectError, setRejectError] = useState("");
  const [withdrawApproveModal, setWithdrawApproveModal] = useState<Timesheet | null>(null);
  const [withdrawApproveComment, setWithdrawApproveComment] = useState("");
  const [withdrawApproveError, setWithdrawApproveError] = useState("");
  const [withdrawRejectModal, setWithdrawRejectModal] = useState<Timesheet | null>(null);
  const [withdrawRejectComment, setWithdrawRejectComment] = useState("");
  const [withdrawRejectError, setWithdrawRejectError] = useState("");
  const [revokeApprovalModal, setRevokeApprovalModal] = useState<Timesheet | null>(null);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const submitApprove = () => {
    if (!approveModal) return;
    setApproveError("");
    approveTimesheet.mutate(
      { id: approveModal.id, comment: approveComment },
      {
        onSuccess: () => { setApproveModal(null); setApproveComment(""); },
        onError: (err: unknown) => {
          const detail = (err as { response?: { data?: { errors?: { detail?: string } } } })
            ?.response?.data?.errors?.detail;
          setApproveError(detail ?? "Błąd podczas zatwierdzania. Spróbuj ponownie.");
        },
      }
    );
  };

  const submitReject = () => {
    if (!rejectModal) return;
    if (!rejectComment.trim()) { setRejectError("Komentarz jest wymagany przy odrzuceniu."); return; }
    setRejectError("");
    rejectTimesheet.mutate(
      { id: rejectModal.id, comment: rejectComment },
      {
        onSuccess: () => { setRejectModal(null); setRejectComment(""); },
        onError: () => setRejectError("Błąd podczas odrzucania. Spróbuj ponownie."),
      }
    );
  };

  const submitApproveWithdrawal = () => {
    if (!withdrawApproveModal) return;
    setWithdrawApproveError("");
    approveWithdrawal.mutate(
      { id: withdrawApproveModal.id, comment: withdrawApproveComment },
      {
        onSuccess: () => { setWithdrawApproveModal(null); setWithdrawApproveComment(""); },
        onError: (err: unknown) => {
          const detail = (err as { response?: { data?: { errors?: { detail?: string } } } })
            ?.response?.data?.errors?.detail;
          setWithdrawApproveError(detail ?? "Błąd podczas akceptacji wycofania. Spróbuj ponownie.");
        },
      }
    );
  };

  const submitRejectWithdrawal = () => {
    if (!withdrawRejectModal) return;
    if (!withdrawRejectComment.trim()) {
      setWithdrawRejectError("Komentarz jest wymagany przy odmowie wycofania.");
      return;
    }
    setWithdrawRejectError("");
    rejectWithdrawal.mutate(
      { id: withdrawRejectModal.id, comment: withdrawRejectComment },
      {
        onSuccess: () => { setWithdrawRejectModal(null); setWithdrawRejectComment(""); },
        onError: () => setWithdrawRejectError("Błąd podczas odmowy wycofania. Spróbuj ponownie."),
      }
    );
  };

  const pendingCount = timesheets.filter((t) => t.status === "pending").length;

  // Group by period for easier reading
  const sorted = [...timesheets].sort((a, b) =>
    b.year !== a.year ? b.year - a.year : b.month !== a.month ? b.month - a.month : a.employee_name.localeCompare(b.employee_name)
  );

  const viewModeLabel: Record<ViewMode, string> = {
    submitted: "Zatwierdzanie i odrzucanie kart godzin pracowników.",
    drafts: "Podgląd roboczych kart godzin, których pracownicy jeszcze nie złożyli.",
    missing: "Pracownicy, którzy nie złożyli jeszcze karty godzin za wybrany okres.",
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Karty godzin — przegląd</h1>
            <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-6" />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {isDirector ? viewModeLabel[viewMode] : "Historia kart godzin pracowników."}
          </p>
          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
            <p className="text-xs text-[var(--color-primary)] font-medium">
              {user?.is_ceo || user?.role === "hr" || user?.role === "accountant"
                ? "Wszystkie oddziały"
                : user?.branch?.name
                ? `Oddział: ${user.branch.name}`
                : null}
            </p>
            {statsEnabled && submissionStats && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-800">
                Karty złożone: {submissionStats.submitted} / {submissionStats.total}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canProxy && proxyTargets.length > 0 && (
            <>
              <Button variant="secondary" onClick={() => { setProxyPdfSearch(""); setProxyPdfPickerOpen(true); }}>
                <Download size={14} className="mr-1.5" /> Generuj kartę pracownika
              </Button>
              <Button variant="secondary" onClick={() => { setProxySubmitSearch(""); setProxySubmitPickerOpen(true); }}>
                <UserPlus size={14} className="mr-1.5" /> Złóż kartę pracownika
              </Button>
            </>
          )}
          {viewMode === "submitted" && pendingCount > 0 && statusFilter === "pending" && isDirector && (
            <span className="text-sm bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 px-3 py-1 rounded-full font-medium">
              {pendingCount} {pendingCount === 1 ? "oczekuje" : "oczekują"}
            </span>
          )}
          {viewMode === "missing" && missingRows.length > 0 && (
            <span className="text-sm bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-3 py-1 rounded-full font-medium">
              {missingRows.length} {missingRows.length === 1 ? "brakująca" : "brakujących"}
            </span>
          )}
        </div>
      </div>

      {/* View mode toggle: złożone / robocze / brakujące */}
      {!isCoordinator && (
        <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 mb-3 w-fit">
          {(["submitted", "drafts", "missing"] as ViewMode[]).map((m) => (
            <button
              key={m}
              onClick={() => setViewMode(m)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
                viewMode === m
                  ? "bg-white dark:bg-gray-700 text-[var(--color-primary)] shadow-sm"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              }`}
            >
              {m === "submitted" && <Clock3 size={13} />}
              {m === "drafts" && <FileEdit size={13} />}
              {m === "missing" && <AlertCircle size={13} />}
              {m === "submitted" ? "Złożone" : m === "drafts" ? "Robocze" : "Brakujące"}
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        {/* Status tabs — only for submitted view */}
        {viewMode === "submitted" && (
          <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
            {(["pending", "withdrawal_requested", "approved", "rejected", "all"] as StatusFilter[]).map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  statusFilter === s
                    ? "bg-white dark:bg-gray-700 text-[var(--color-primary)] shadow-sm"
                    : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                }`}
              >
                {s === "pending" ? "Oczekujące"
                  : s === "withdrawal_requested" ? "Wycofania"
                  : s === "approved" ? "Zatwierdzone"
                  : s === "rejected" ? "Odrzucone"
                  : "Wszystkie"}
              </button>
            ))}
          </div>
        )}

        {/* Year filter */}
        <select
          className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-1.5 text-sm"
          value={yearFilter}
          onChange={(e) => setYearFilter(e.target.value)}
        >
          {[CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2].map((y) => (
            <option key={y} value={String(y)}>{y}</option>
          ))}
        </select>

        {/* Month filter — required in drafts/missing mode (no "all months" option there) */}
        <select
          className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-1.5 text-sm"
          value={
            viewMode !== "submitted" ? String(draftMonth) : monthFilter
          }
          onChange={(e) => setMonthFilter(e.target.value)}
        >
          {viewMode === "submitted" && <option value="">Wszystkie miesiące</option>}
          {Object.entries(MONTH_NAMES).map(([num, name]) => (
            <option key={num} value={num}>{name}</option>
          ))}
        </select>

        {/* Employee filter — only submitted view for non-coordinator */}
        {viewMode === "submitted" && !isCoordinator && (
          <SearchableSelect
            placeholder="Wszyscy pracownicy"
            value={employeeFilter}
            onChange={(e) => setEmployeeFilter(e.target.value)}
            options={internalUsers.map((u) => ({ value: u.id, label: u.full_name }))}
          />
        )}
      </div>

      {/* Missing panel */}
      {viewMode === "missing" && (
        missingLoading ? (
          <PageLoader />
        ) : missingRows.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 p-8 text-center">
            <CheckCircle2 size={32} className="text-green-400 mx-auto mb-2" />
            <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">
              Wszyscy pracownicy złożyli karty godzin za ten okres.
            </p>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/60 text-xs uppercase text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Pracownik</th>
                  <th className="px-3 py-2 text-left font-medium">Login</th>
                  <th className="px-3 py-2 text-left font-medium">Oddział</th>
                  <th className="px-3 py-2 text-left font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {missingRows.map((row) => (
                  <tr key={row.employee_id} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
                    <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">
                      {row.full_name}
                    </td>
                    <td className="px-3 py-2 text-gray-500 dark:text-gray-400 text-xs">
                      {row.username}
                    </td>
                    <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                      {row.branch_name ?? "—"}
                    </td>
                    <td className="px-3 py-2">
                      {row.has_rejected ? (
                        <Badge color="red">odrzucona</Badge>
                      ) : (
                        <Badge color="gray">brak</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Drafts panel */}
      {viewMode === "drafts" && (
        draftsLoading ? (
          <PageLoader />
        ) : draftRows.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 p-8 text-center">
            <FileEdit size={32} className="text-gray-300 dark:text-gray-600 mx-auto mb-2" />
            <p className="text-gray-400 dark:text-gray-500 text-sm">
              Nikt z {user?.is_ceo || user?.role === "hr" || user?.role === "accountant"
                ? "pracowników"
                : "Twojego oddziału"}{" "}
              nie ma jeszcze zapisanego szkicu karty za ten okres.
            </p>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/60 text-xs uppercase text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Pracownik</th>
                  <th className="px-3 py-2 text-left font-medium">Oddział</th>
                  <th className="px-3 py-2 text-left font-medium">Podstawowa</th>
                  <th className="px-3 py-2 text-left font-medium">Dodatkowe (UZ)</th>
                  <th className="px-3 py-2 text-left font-medium">Złożona?</th>
                  <th className="px-3 py-2 text-right font-medium">Akcje</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {draftRows.map((row) => {
                  const primary = row.primary_draft;
                  const secondaries = row.secondary_drafts;
                  return (
                    <tr key={row.employee_id} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
                      <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">
                        {row.employee_name}
                      </td>
                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                        {row.branch_name ?? "—"}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <Badge color={row.primary_contract_kind === "uop" ? "gray" : "blue"}>
                            {row.primary_contract_kind.toUpperCase()}
                          </Badge>
                          {primary ? (
                            <span className="text-xs text-gray-600 dark:text-gray-300">
                              {primary.default_shift ? `${primary.default_shift}h` : "—"}{" "}
                              <span className="text-gray-400">
                                · {new Date(primary.updated_at).toLocaleDateString("pl-PL")}
                              </span>
                            </span>
                          ) : (
                            <span className="text-xs text-gray-300">brak</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        {secondaries.length === 0 ? (
                          <span className="text-xs text-gray-300">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {secondaries.map((s) => (
                              <Badge key={s.contract_id ?? "x"} color="blue">
                                {(s.contract_kind ?? "uz").toUpperCase()}
                                {s.declared_hours ? ` ${s.declared_hours}h` : ""}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {row.submitted_primary_status ? (
                          <Badge color={TIMESHEET_STATUS_COLORS[row.submitted_primary_status]}>
                            Tak ·{" "}
                            {row.submitted_primary_status === "pending" ? "oczekuje" :
                              row.submitted_primary_status === "approved" ? "zatw." :
                              row.submitted_primary_status === "withdrawal_requested" ? "wycofanie" :
                              row.submitted_primary_status === "withdrawn" ? "wycofana" :
                              "odrzuc."}
                          </Badge>
                        ) : (
                          <span className="text-xs text-gray-400">Nie</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex justify-end gap-1.5 flex-wrap">
                          {primary && (
                            <button
                              onClick={() =>
                                setDraftPreview({
                                  employeeId: row.employee_id,
                                  employeeName: row.employee_name,
                                  secondaryContractId: null,
                                  secondaryContractKind: null,
                                })
                              }
                              className="flex items-center gap-1 text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-2 py-1 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 font-medium"
                            >
                              <Eye size={11} /> Podstawowa
                            </button>
                          )}
                          {secondaries.map((s) => (
                            <button
                              key={s.contract_id ?? "x"}
                              onClick={() =>
                                setDraftPreview({
                                  employeeId: row.employee_id,
                                  employeeName: row.employee_name,
                                  secondaryContractId: s.contract_id,
                                  secondaryContractKind: s.contract_kind,
                                })
                              }
                              className="flex items-center gap-1 text-xs bg-white dark:bg-gray-700 border border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 font-medium"
                            >
                              <Eye size={11} /> {(s.contract_kind ?? "uz").toUpperCase()}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Submitted panel */}
      {viewMode === "submitted" && (
        isLoading ? (
          <PageLoader />
        ) : sorted.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 p-8 text-center">
            <Clock3 size={32} className="text-gray-300 dark:text-gray-600 mx-auto mb-2" />
            <p className="text-gray-400 dark:text-gray-500 text-sm">Brak kart godzin w tej kategorii.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {sorted.map((ts) => {
              const isOpen = expanded.has(ts.id);
              return (
                <div
                  key={ts.id}
                  className="bg-white dark:bg-gray-800 overflow-hidden"
                >
                  {/* Row header */}
                  <div className="flex items-center gap-3 px-4 py-3">
                    <button
                      onClick={() => toggle(ts.id)}
                      className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                    >
                      {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                          {ts.employee_name}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {ts.period_display}
                        </span>
                        <Badge color={TIMESHEET_STATUS_COLORS[ts.status as TimesheetStatus]}>
                          {ts.status === "approved" && <CheckCircle2 size={11} className="mr-1" />}
                          {ts.status === "rejected" && <XCircle size={11} className="mr-1" />}
                          {ts.status === "pending" && <Clock size={11} className="mr-1" />}
                          {ts.status === "withdrawal_requested" && <RefreshCw size={11} className="mr-1" />}
                          {ts.status === "withdrawn" && <Undo2 size={11} className="mr-1" />}
                          {ts.status_display}
                        </Badge>
                        {ts.replaces && ts.status === "pending" && (
                          <Badge color="orange">wymiana</Badge>
                        )}
                        {ts.submitted_by_name && (
                          <span className="text-xs text-gray-400 dark:text-gray-500 italic">
                            złożone przez {ts.submitted_by_name}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                        złożono {new Date(ts.created_at).toLocaleDateString("pl-PL")}
                      </p>
                    </div>

                    {/* Quick actions — director only */}
                    {isDirector && (
                      <div className="flex gap-2 flex-shrink-0">
                        {ts.status === "pending" && (
                          <>
                            <button
                              onClick={() => { setApproveComment(""); setApproveError(""); setApproveModal(ts); }}
                              className="flex items-center gap-1 text-xs bg-green-600 text-white px-2.5 py-1.5 rounded-md hover:bg-green-700 font-medium"
                            >
                              <CheckCircle2 size={12} /> Zatwierdź
                            </button>
                            <button
                              onClick={() => { setRejectComment(""); setRejectError(""); setRejectModal(ts); }}
                              className="flex items-center gap-1 text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-2.5 py-1.5 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 font-medium"
                            >
                              <XCircle size={12} /> Odrzuć
                            </button>
                          </>
                        )}
                        {ts.status === "withdrawal_requested" && (
                          <>
                            <button
                              onClick={() => { setWithdrawApproveComment(""); setWithdrawApproveError(""); setWithdrawApproveModal(ts); }}
                              className="flex items-center gap-1 text-xs bg-orange-600 text-white px-2.5 py-1.5 rounded-md hover:bg-orange-700 font-medium"
                            >
                              <RefreshCw size={12} /> Akceptuj wycofanie
                            </button>
                            <button
                              onClick={() => { setWithdrawRejectComment(""); setWithdrawRejectError(""); setWithdrawRejectModal(ts); }}
                              className="flex items-center gap-1 text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-2.5 py-1.5 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 font-medium"
                            >
                              <XCircle size={12} /> Odmów
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => setDeleteId(ts.id)}
                          className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-500 px-2 py-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          title="Usuń"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    )}

                    {/* Undo an approval — CEO only, and only on an approved card.
                        Kept out of the isDirector block on purpose: a plain
                        director must never see it, and a CEO whose role label is
                        not "director" must. */}
                    {isCeo && ts.status === "approved" && (
                      <div className="flex gap-2 flex-shrink-0">
                        <button
                          onClick={() => setRevokeApprovalModal(ts)}
                          className="flex items-center gap-1 text-xs bg-white dark:bg-gray-700 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 px-2.5 py-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 font-medium"
                        >
                          <Undo2 size={12} /> Cofnij zatwierdzenie
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Expanded details */}
                  {isOpen && (
                    <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-4 bg-gray-50 dark:bg-gray-800/50 text-sm flex flex-col gap-2">
                      {ts.note && (
                        <p className="text-gray-600 dark:text-gray-400">
                          <span className="font-medium text-gray-700 dark:text-gray-300">Uwagi: </span>
                          {ts.note}
                        </p>
                      )}
                      {ts.review_comment && (
                        <p className={ts.status === "rejected" ? "text-red-600" : "text-green-700 dark:text-green-400"}>
                          <span className="font-medium">Komentarz: </span>
                          {ts.review_comment}
                        </p>
                      )}
                      {ts.reviewed_by_name && (
                        <p className="text-gray-500 dark:text-gray-400 text-xs">
                          Decyzja: {ts.reviewed_by_name}
                          {ts.reviewed_at && ` · ${new Date(ts.reviewed_at).toLocaleString("pl-PL")}`}
                        </p>
                      )}
                      {ts.approval_revoked_by_name && (
                        <p className="text-red-600 dark:text-red-400 text-xs">
                          <span className="font-medium">Cofnięto zatwierdzenie: </span>
                          {ts.approval_revoked_by_name}
                          {ts.approval_revoked_at &&
                            ` · ${new Date(ts.approval_revoked_at).toLocaleString("pl-PL")}`}
                          {ts.approval_revocation_reason && ` — ${ts.approval_revocation_reason}`}
                          {ts.approval_revoked_over_settled_payroll && (
                            <span className="font-medium"> (wypłaty były już rozliczone)</span>
                          )}
                        </p>
                      )}
                      {ts.status === "withdrawal_requested" && (
                        <p className="text-orange-600 dark:text-orange-400 text-xs">
                          Pracownik złożył nową kartę za ten okres — zaakceptuj wycofanie tej karty,
                          a następnie zatwierdź nową.
                        </p>
                      )}
                      {ts.withdrawal_comment && (
                        <p className="text-orange-600 dark:text-orange-400 text-xs">
                          <span className="font-medium">Wycofanie: </span>
                          {ts.withdrawal_comment}
                        </p>
                      )}
                      {ts.withdrawal_decided_by_name && (
                        <p className="text-gray-500 dark:text-gray-400 text-xs">
                          Decyzja o wycofaniu: {ts.withdrawal_decided_by_name}
                          {ts.withdrawal_decided_at && ` · ${new Date(ts.withdrawal_decided_at).toLocaleString("pl-PL")}`}
                        </p>
                      )}
                      {ts.scan_managed_file_detail?.url && (
                        <>
                          <a
                            href={ts.scan_managed_file_detail.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 text-[var(--color-primary)] hover:underline w-fit"
                          >
                            <FileText size={14} /> Pobierz skan karty
                          </a>
                          {ts.scan_managed_file_detail.url.toLowerCase().split("?")[0].endsWith(".pdf") ? (
                            <iframe
                              src={ts.scan_managed_file_detail.inline_url ?? ts.scan_managed_file_detail.url}
                              title="Skan karty godzin"
                              className="w-full rounded mt-2"
                              style={{ height: "600px" }}
                            />
                          ) : (
                            <img
                              src={ts.scan_managed_file_detail.inline_url ?? ts.scan_managed_file_detail.url}
                              alt="Skan karty godzin"
                              className="max-w-full rounded mt-2"
                            />
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}

      {/* Approve modal */}
      <Modal open={!!approveModal} onClose={() => setApproveModal(null)} title="Zatwierdź kartę godzin">
        <div className="flex flex-col gap-4">
          {approveModal && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-sm">
              <p className="font-medium text-green-800 dark:text-green-300">{approveModal.employee_name}</p>
              <p className="text-green-700 dark:text-green-400 text-xs mt-0.5">{approveModal.period_display}</p>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Komentarz <span className="text-gray-400 text-xs">(opcjonalny)</span>
            </label>
            <textarea
              rows={2}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              value={approveComment}
              onChange={(e) => setApproveComment(e.target.value)}
              placeholder="Dodatkowe uwagi..."
            />
          </div>
          {approveError && <p className="text-sm text-red-600">{approveError}</p>}
          <div className="flex gap-3">
            <Button loading={approveTimesheet.isPending} onClick={submitApprove}>
              <CheckCircle2 size={14} className="mr-1.5" /> Zatwierdź
            </Button>
            <Button variant="secondary" onClick={() => setApproveModal(null)}>Anuluj</Button>
          </div>
        </div>
      </Modal>

      {/* Delete modal — director only */}
      <Modal open={!!deleteId} onClose={() => setDeleteId(null)} title="Usuń kartę godzin">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Czy na pewno chcesz usunąć tę kartę godzin? Tej operacji nie można cofnąć.
          </p>
          <div className="flex gap-3">
            <Button
              variant="secondary"
              loading={deleteTimesheet.isPending}
              onClick={() => {
                if (!deleteId) return;
                deleteTimesheet.mutate(deleteId, { onSuccess: () => setDeleteId(null) });
              }}
            >
              <Trash2 size={14} className="mr-1.5" /> Usuń
            </Button>
            <Button variant="secondary" onClick={() => setDeleteId(null)}>Anuluj</Button>
          </div>
        </div>
      </Modal>

      {/* Draft preview modal (read-only) */}
      {draftPreview && (
        <DraftPreviewModal
          open
          onClose={() => setDraftPreview(null)}
          employeeId={draftPreview.employeeId}
          employeeName={draftPreview.employeeName}
          year={draftYear}
          month={draftMonth}
          secondaryContractId={draftPreview.secondaryContractId}
          secondaryContractKind={draftPreview.secondaryContractKind}
        />
      )}

      {/* Reject modal */}
      <Modal open={!!rejectModal} onClose={() => setRejectModal(null)} title="Odrzuć kartę godzin">
        <div className="flex flex-col gap-4">
          {rejectModal && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm">
              <p className="font-medium text-red-800 dark:text-red-300">{rejectModal.employee_name}</p>
              <p className="text-red-700 dark:text-red-400 text-xs mt-0.5">{rejectModal.period_display}</p>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Powód odrzucenia <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={3}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              value={rejectComment}
              onChange={(e) => setRejectComment(e.target.value)}
              placeholder="Uzasadnienie odrzucenia karty godzin..."
            />
            {rejectError && <p className="text-xs text-red-500 mt-1">{rejectError}</p>}
          </div>
          <div className="flex gap-3">
            <Button loading={rejectTimesheet.isPending} onClick={submitReject} variant="secondary">
              <XCircle size={14} className="mr-1.5" /> Odrzuć
            </Button>
            <Button variant="secondary" onClick={() => setRejectModal(null)}>Anuluj</Button>
          </div>
        </div>
      </Modal>

      {/* Approve withdrawal modal */}
      <Modal
        open={!!withdrawApproveModal}
        onClose={() => setWithdrawApproveModal(null)}
        title="Akceptuj wycofanie karty godzin"
      >
        <div className="flex flex-col gap-4">
          {withdrawApproveModal && (
            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3 text-sm">
              <p className="font-medium text-orange-800 dark:text-orange-300">{withdrawApproveModal.employee_name}</p>
              <p className="text-orange-700 dark:text-orange-400 text-xs mt-0.5">{withdrawApproveModal.period_display}</p>
            </div>
          )}
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Karta zostanie oznaczona jako <span className="font-medium">wycofana</span>.
            Nowa karta złożona przez pracownika czeka na osobne zatwierdzenie.
          </p>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Komentarz <span className="text-gray-400 text-xs">(opcjonalny)</span>
            </label>
            <textarea
              rows={2}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              value={withdrawApproveComment}
              onChange={(e) => setWithdrawApproveComment(e.target.value)}
              placeholder="Dodatkowe uwagi..."
            />
          </div>
          {withdrawApproveError && <p className="text-sm text-red-600">{withdrawApproveError}</p>}
          <div className="flex gap-3">
            <Button loading={approveWithdrawal.isPending} onClick={submitApproveWithdrawal}>
              <RefreshCw size={14} className="mr-1.5" /> Akceptuj wycofanie
            </Button>
            <Button variant="secondary" onClick={() => setWithdrawApproveModal(null)}>Anuluj</Button>
          </div>
        </div>
      </Modal>

      {/* Reject withdrawal modal */}
      <Modal
        open={!!withdrawRejectModal}
        onClose={() => setWithdrawRejectModal(null)}
        title="Odmów wycofania karty godzin"
      >
        <div className="flex flex-col gap-4">
          {withdrawRejectModal && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm">
              <p className="font-medium text-red-800 dark:text-red-300">{withdrawRejectModal.employee_name}</p>
              <p className="text-red-700 dark:text-red-400 text-xs mt-0.5">{withdrawRejectModal.period_display}</p>
            </div>
          )}
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Karta wróci do statusu <span className="font-medium">zatwierdzona</span>,
            a nowa (zastępcza) karta pracownika zostanie odrzucona z poniższym komentarzem.
          </p>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Powód odmowy <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={3}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              value={withdrawRejectComment}
              onChange={(e) => setWithdrawRejectComment(e.target.value)}
              placeholder="Uzasadnienie odmowy wycofania..."
            />
            {withdrawRejectError && <p className="text-xs text-red-500 mt-1">{withdrawRejectError}</p>}
          </div>
          <div className="flex gap-3">
            <Button loading={rejectWithdrawal.isPending} onClick={submitRejectWithdrawal} variant="secondary">
              <XCircle size={14} className="mr-1.5" /> Odmów wycofania
            </Button>
            <Button variant="secondary" onClick={() => setWithdrawRejectModal(null)}>Anuluj</Button>
          </div>
        </div>
      </Modal>

      {/* Revoke approval modal — CEO only */}
      <RevokeApprovalModal
        timesheet={revokeApprovalModal}
        onClose={() => setRevokeApprovalModal(null)}
      />

      {/* Proxy PDF — employee picker */}
      <Modal
        open={proxyPdfPickerOpen}
        onClose={() => { setProxyPdfPickerOpen(false); setProxyPdfSearch(""); }}
        title="Generuj kartę godzin — wybierz pracownika"
        size="lg"
      >
        <EmployeePickerContent
          targets={proxyTargets}
          search={proxyPdfSearch}
          onSearchChange={setProxyPdfSearch}
          onSelect={(t) => { setProxyPdfEmployee({ id: t.id, name: t.full_name }); setProxyPdfPickerOpen(false); setProxyPdfSearch(""); }}
          onCancel={() => { setProxyPdfPickerOpen(false); setProxyPdfSearch(""); }}
        />
      </Modal>

      {/* Proxy PDF — generator modal */}
      {proxyPdfEmployee && (
        <TimesheetPdfModal
          open={!!proxyPdfEmployee}
          onClose={() => setProxyPdfEmployee(null)}
          forEmployee={proxyPdfEmployee}
        />
      )}

      {/* Proxy Submit — employee picker */}
      <Modal
        open={proxySubmitPickerOpen}
        onClose={() => { setProxySubmitPickerOpen(false); setProxySubmitSearch(""); }}
        title="Złóż kartę godzin — wybierz pracownika"
        size="lg"
      >
        <EmployeePickerContent
          targets={proxyTargets}
          search={proxySubmitSearch}
          onSearchChange={setProxySubmitSearch}
          onSelect={(t) => { setProxySubmitEmployee({ id: t.id, name: t.full_name }); setProxySubmitPickerOpen(false); setProxySubmitSearch(""); }}
          onCancel={() => { setProxySubmitPickerOpen(false); setProxySubmitSearch(""); }}
        />
      </Modal>

      {/* Proxy Submit — submission modal */}
      {proxySubmitEmployee && (
        <ProxySubmitModal
          open={!!proxySubmitEmployee}
          onClose={() => setProxySubmitEmployee(null)}
          employee={proxySubmitEmployee}
        />
      )}
    </div>
  );
}
